import { refinementList } from 'instantsearch.js/es/widgets/index.js'

const SEARCH_VISIBLE_FACET_LIMIT = 12
const SEARCH_OPEN_FACET_LIMIT = 4

function createSafeId(attribute, index) {
  const slug = attribute
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

  return `category-facet-${slug}-${index}`
}

function createFacetAccordion({ attribute, label, index, isOpen }) {
  const id = createSafeId(attribute, index)

  const accordionItem = document.createElement('div')
  accordionItem.className = 'accordion-item'
  accordionItem.dataset.facetAttribute = attribute
  accordionItem.dataset.facetIndex = String(index)

  const heading = document.createElement('h3')
  heading.className = 'accordion-header'
  heading.id = `${id}-heading`

  const button = document.createElement('button')
  button.type = 'button'
  button.className = `accordion-button px-0 py-3${isOpen ? '' : ' collapsed'}`
  button.setAttribute('data-bs-toggle', 'collapse')
  button.setAttribute('data-bs-target', `#${id}`)
  button.setAttribute('aria-expanded', String(isOpen))
  button.setAttribute('aria-controls', id)
  button.textContent = label

  const collapse = document.createElement('div')
  collapse.id = id
  collapse.className = `accordion-collapse collapse${isOpen ? ' show' : ''}`
  collapse.setAttribute('aria-labelledby', `${id}-heading`)

  const body = document.createElement('div')
  body.className = 'accordion-body px-0 pt-0 pb-3'

  const valuesContainer = document.createElement('div')
  valuesContainer.dataset.facetValues = ''

  body.append(valuesContainer)
  collapse.append(body)
  heading.append(button)
  accordionItem.append(heading, collapse)

  return {
    accordionItem,
    valuesContainer,
  }
}

function createSearchFacetToggle(refinementContainer, overflowFacets, translations) {
  if (overflowFacets.length === 0) {
    return
  }

  const rootElement = refinementContainer.closest('[data-product-listing="search"]')
  const showMoreLabel = rootElement?.dataset.showMoreFiltersText ?? translations.showMore
  const showLessLabel = rootElement?.dataset.showLessFiltersText ?? translations.showLess
  const button = document.createElement('button')
  const label = document.createElement('span')
  const icon = document.createElement('i')
  let isExpanded = false

  button.type = 'button'
  button.className = 'btn btn-link btn-sm text-decoration-none px-0 py-3 w-100 d-flex align-items-center justify-content-between border-top'
  icon.className = 'ci-chevron-down fs-xs ms-2'
  icon.setAttribute('aria-hidden', 'true')

  const render = () => {
    overflowFacets.forEach((facet) => facet.classList.toggle('d-none', !isExpanded))
    label.textContent = isExpanded ? showLessLabel : showMoreLabel
    icon.className = `${isExpanded ? 'ci-chevron-up' : 'ci-chevron-down'} fs-xs ms-2`
    button.setAttribute('aria-expanded', String(isExpanded))
  }

  button.addEventListener('click', () => {
    isExpanded = !isExpanded
    render()
  })

  button.append(label, icon)
  refinementContainer.append(button)
  render()
}

export function createFacetWidgets({ facets, refinementContainer, getFacetLabel, translations }) {
  const isSearchListing = Boolean(refinementContainer.closest('[data-product-listing="search"]'))
  const overflowFacets = []

  const widgets = facets.map((attribute, index) => {
    const isOpen = !isSearchListing || index < SEARCH_OPEN_FACET_LIMIT
    const { accordionItem, valuesContainer } = createFacetAccordion({
      attribute,
      label: getFacetLabel(attribute),
      index,
      isOpen,
    })

    if (isSearchListing && index >= SEARCH_VISIBLE_FACET_LIMIT) {
      accordionItem.classList.add('d-none')
      overflowFacets.push(accordionItem)
    }

    refinementContainer.append(accordionItem)

    return refinementList({
      container: valuesContainer,
      attribute,
      operator: 'or',
      limit: 8,
      showMore: true,
      showMoreLimit: 30,
      sortBy: ['isRefined:desc', 'count:desc', 'name:asc'],
      templates: {
        showMoreText({ isShowingMore }, { html }) {
          return html`
            <span> ${isShowingMore ? translations.showLess : translations.showMore} </span>

            <i class="${isShowingMore ? 'ci-chevron-up' : 'ci-chevron-down'} fs-xs ms-1"></i>
          `
        },
      },
      cssClasses: {
        root: 'w-100',
        noRefinementRoot: 'd-none',
        noResults: 'text-body-secondary fs-sm',
        list: ['list-unstyled', 'd-flex', 'flex-column', 'gap-2', 'mb-0'],
        item: 'mb-0',
        selectedItem: 'fw-semibold',
        label: ['form-check', 'd-flex', 'align-items-center', 'gap-2', 'mb-0'],
        checkbox: ['form-check-input', 'flex-shrink-0', 'mt-0'],
        labelText: ['form-check-label', 'flex-grow-1'],
        count: ['badge', 'rounded-pill', 'bg-body-secondary', 'text-body-secondary'],
        showMore: ['btn', 'btn-link', 'btn-sm', 'text-decoration-none', 'px-0', 'mt-3'],
        disabledShowMore: 'd-none',
      },
    })
  })

  if (isSearchListing) {
    createSearchFacetToggle(refinementContainer, overflowFacets, translations)
  }

  return widgets
}
